import { useTranslation } from '../../../lib/i18n';
import { messageDir } from '../../../lib/message-direction';
import type { SentOutreachSummary } from '../../../lib/use-sent-outreaches';
import { Badge, Card, Typography } from '../ui';
import styles from './TaskSentOutreachSection.module.css';

/**
 * What became of the people this task reached out to.
 *
 * A task that messaged somebody used to end at "Done" and say nothing about the outcome — "Done" means
 * *the agent sent it*, not *they answered*, and the detail gave no way to tell those apart. The API has
 * computed this per task all along (`summariseSentOutreach`) and no screen read it.
 *
 * The one fact worth leading with is **whether they wrote back**, so it is a badge rather than a line of
 * prose: `waiting-on-a-reply` while the thread is open and unanswered, `done` once they have replied. The
 * last thing said sits under it, so a glance answers "did this land" without opening anything.
 *
 * Renders nothing when the task sent no outreach — no title, no empty state — matching how the Files
 * section beside it behaves.
 *
 * The channel each one went out on is deliberately NOT shown yet. The summary carries it, but there is no
 * channel label set in the catalogs, and inventing one means eight keys across 37 bundles for a subtitle
 * — disproportionate to this first cut, and a decision worth making once for every surface that wants it.
 */
export function TaskSentOutreachSection({ outreaches }: { outreaches?: SentOutreachSummary[] }) {
  const { t } = useTranslation();
  if (!outreaches || outreaches.length === 0) return null;

  return (
    <section className={styles.section} aria-label={t('tasks.sentOutreach.title')}>
      <Typography variant="h4" component="h3">
        {t('tasks.sentOutreach.title')}
      </Typography>
      <div className={styles.list}>
        {outreaches.map((o) => {
          // The recipient's name when the sender's own assistant resolved one. Unlike the inbound side
          // there is no "Message from X:" prefix to fall back on, so an unresolved name says so plainly
          // rather than inventing a placeholder that reads like a real person.
          const who = o.recipientName?.trim() || t('tasks.sentOutreach.unnamedRecipient');
          return (
            <Card key={o.id} variant="stone" size="sm" className={styles.row}>
              <div className={styles.head}>
                <Typography variant="p2" component="span" noWrap className={styles.who} dir={messageDir(who ?? '')}>
                  {who}
                </Typography>
                {/* Open and unanswered vs. they replied. A settled thread that never got an answer shows
                    neither — it is closed, and "no reply yet" would be a claim about a finished exchange. */}
                {o.awaitingReply ? (
                  <Badge variant="waiting-on-a-reply">{t('tasks.sentOutreach.awaitingReply')}</Badge>
                ) : o.replied ? (
                  <Badge variant="done">{t('tasks.sentOutreach.replied')}</Badge>
                ) : null}
              </div>
              {o.lastMessage ? (
                <Typography variant="p2" component="p" color="secondary" className={styles.last} dir={messageDir(o.lastMessage ?? '')}>
                  {o.lastMessage}
                </Typography>
              ) : null}
            </Card>
          );
        })}
      </div>
    </section>
  );
}
